import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'
import DefaultPagination from "../../Pagination/DefaultPagination";
import useApi from "../../../utility/Hooks/Api/useApi";
import { CompanyApi } from "../../../services/Api/CompanyApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCarDown, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { use } from 'react';

let PageSize = 20;

const companyKeys = ['customerId', 'assemblyCompanyId', 'installationCompanyId'];

export default function DefaultTable({ data, column, companyIdKey,
    onChange, deletionItems, onNavDetail,
    order, checkBox, pageType, }) {
    const [currentPage, setCurrentPage] = useState(1);
    const { request, data: companies } = useApi();
    const { request: companyTypesRequest, data: companyTypes } = useApi();
    const navigate = useNavigate();
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });


    const [columnOrder, setColumnOrder] = useState(column);

    useEffect(() => {
        if (companyIdKey) {
            request(CompanyApi.getCompanies());
        }
        companyTypesRequest(CompanyApi.getTypes());
    }, []);

    useEffect(() => {
        setCurrentPage(1)
    }, [data])

    useEffect(() => {
        // Load the saved column order from localStorage
        const savedColumnOrder = localStorage.getItem(`columnOrder-${pageType}`);
        if (savedColumnOrder) {
            setColumnOrder(JSON.parse(savedColumnOrder));
        }
    }, [pageType]);


    const handleDragStart = (e, index) => {
        e.dataTransfer.setData("columnIndex", index);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, index) => {
        e.preventDefault();
        const draggedColumnIndex = e.dataTransfer.getData("columnIndex");
        if (draggedColumnIndex !== null) {
            const newOrder = [...columnOrder];
            const [movedColumn] = newOrder.splice(draggedColumnIndex, 1);
            newOrder.splice(index, 0, movedColumn);
            setColumnOrder(newOrder);
            // Save the new column order to localStorage
            localStorage.setItem(`columnOrder-${pageType}`, JSON.stringify(newOrder));
        }
    };


    const getCaseInsensitive = (obj, key) => {
        if (!obj) return undefined;
        const lowerCaseKey = key.toLowerCase();
        return Object.keys(obj).reduce((result, currentKey) => {
            return currentKey.toLowerCase() === lowerCaseKey ? obj[currentKey] : result;
        }, undefined);
    };

    const getCompanyName = useCallback((item, key) => {
        const company = companies?.find(function (element) {
            return element["id"]?.toString() === getCaseInsensitive(item, key)?.toString() || getCaseInsensitive(element, 'customer id') === getCaseInsensitive(item, key)?.toString();
        });
        return company ? getCaseInsensitive(company, 'company name') : "";
    }, [companies]);

    const getCompanyType = (item) => {
        const type = companyTypes?.find(function (element) {
            return element["id"]?.toString() === item?.toString();
        });
        return type ? type["name"] : "";
    };

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            const aValue = companyKeys.includes(sortConfig.key)
                ? getCompanyName(a, sortConfig.key)
                : getCaseInsensitive(a, sortConfig.key);
            const bValue = companyKeys.includes(sortConfig.key)
                ? getCompanyName(b, sortConfig.key)
                : getCaseInsensitive(b, sortConfig.key);

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortConfig.direction === 'ascending' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig, getCompanyName]);

    const currentTableData = useMemo(() => {
        if (sortedData) {
            const firstPageIndex = (currentPage - 1) * PageSize;
            const lastPageIndex = firstPageIndex + PageSize;
            return sortedData.slice(firstPageIndex, lastPageIndex);
        }
    }, [currentPage, sortedData]);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        } else if (sortConfig.key === key && sortConfig.direction === 'descending') {
            direction = 'ascending';
        }
        setSortConfig({ key, direction });
    };

    const TableHeadItem = ({ item }) => (
        <th
            scope="col"
            className="px-5 py-2 bg-white border-b-2 border-t border-gray-200 text-gray-800 text-left text-sm font-semibold border-r cursor-pointer"
            onClick={() => handleSort(item.value)}
        >
            {item.heading}
            {sortConfig.key === item.value ? (sortConfig.direction === 'ascending' ?
                <FontAwesomeIcon
                    icon={faSortUp}
                    className="h-5 w-5 text-gray-500 ml-2 bottom"
                />
                : <FontAwesomeIcon
                    icon={faSortDown}
                    className="h-5 w-5 text-gray-500 ml-2"
                />) : null}
        </th>
    );


    return (
        <>
            {!data && <div
                className="absolute w-full h-full ml-auto mr-auto px-4 flex flex-col justify-center items-center">
                <svg className="animate-spin h-5 w-5 text-indigo-600"
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                        strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>}
            <div className="-mx-4 sm:-mx-8 px-4 sm:px-8 overflow-hidden">
                <div className="min-w-full shadow overflow-x-auto min-h-[38.7rem]">
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                {columnOrder.map((col, index) => (
                                    <th
                                        key={col.value}
                                        scope="col"
                                        className="px-5 py-2 bg-white border-b-2 border-t border-gray-200 text-gray-800 text-left text-sm font-semibold border-r cursor-pointer"
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, index)}
                                        onClick={() => handleSort(col.value)}
                                    >
                                        {col.heading}
                                        {sortConfig.key === col.value && (
                                            <FontAwesomeIcon
                                                icon={sortConfig.direction === "ascending" ? faSortUp : faSortDown}
                                                className="h-5 w-5 text-gray-500 ml-2"
                                            />
                                        )}
                                    </th>
                                ))}
                                {order && <th scope="col"
                                    className="px-5 py-2 bg-white border-b-2  border-t border-gray-200 text-gray-800  text-left text-sm font-semibold border-r">
                                    Progress
                                </th>}
                                {checkBox && <th scope="col"
                                    className="px-5 py-2 bg-white border-b-2  border-t border-gray-200 text-gray-800  text-left text-sm font-semibold">
                                    Action
                                </th>}
                            </tr>
                        </thead>
                        <tbody>{data && currentTableData.map((item, index) => <TableRow
                            item={item}
                            columnOrder={columnOrder}
                            order={order}
                            rowIndex={index}
                            companyIdKey={companyIdKey}
                            getCompanyName={getCompanyName}
                            getCompanyType={getCompanyType}
                            onChange={(e) => onChange(e)}
                            checkBox={checkBox}
                            checked={deletionItems?.find(e => e === item["id"])}
                            onNavDetail={() => navigate(`${onNavDetail}${item["id"]}`)} />)}




                        </tbody>
                    </table>
                </div>
                {
                    data && <DefaultPagination
                        currentPage={currentPage}
                        totalCount={data.length}
                        pageSize={PageSize}
                        onPageChange={page => setCurrentPage(page)}
                    />
                }
            </div>
        </>
    )
}

const getCaseInsensitive = (obj, key) => {
    if (!obj) return undefined;
    const lowerCaseKey = key.toLowerCase();
    return Object.keys(obj).reduce((result, currentKey) => {
        return currentKey.toLowerCase() === lowerCaseKey ? obj[currentKey] : result;
    }, undefined);
};

const TableRow = ({
    columnOrder, item, rowIndex,
    companyIdKey, getCompanyName, getCompanyType,
    onChange, checked, onNavDetail,
    order, checkBox
}) => {
    return (
        <tr className={`cursor-pointer hover:bg-blue-600/20 ${rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`} onClick={onNavDetail}>
            {columnOrder.map((columnItem) => {
                if (columnItem.value.includes(".")) {
                    const itemSplit = columnItem.value.split(".");
                    return <td
                        className={`px-5 py-1 border-b border-gray-200 text-sm font-base`}>
                        <div className="flex items-center">
                            <div className="ml-3">
                                <p className="text-gray-900 whitespace-no-wrap">
                                    {item[itemSplit[0]][itemSplit[1]]}
                                </p>
                            </div>
                        </div>
                    </td>
                }
                if (Array.isArray(item[columnItem.value])) {
                    const detailsArray = item[columnItem.value];
                    return (
                        <td className="px-5 py-1 border-b border-gray-200 text-sm font-base relative" key={columnItem.value}>
                            <div className="group relative">
                                <svg
                                    className="w-5 h-5 text-blue-600"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                                </svg>

                                {/* Tooltip */}
                                <div className="absolute z-50 hidden group-hover:block left-1/2 -translate-x-1/2 top-7 w-max max-w-sm bg-white text-gray-800 border border-gray-300 rounded-md shadow-lg p-3 text-sm whitespace-nowrap">
                                    {detailsArray.map((entry, index) => (
                                        <div key={index}>
                                            <span className="font-medium">{entry.key}:</span> {entry.value}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </td>
                    );
                }

                return <td
                    className={`px-5 py-1 border-b border-gray-200 text-sm font-base`}>
                    <div className="flex items-center">
                        <div className="ml-3">
                            <p className="text-gray-900 whitespace-no-wrap">
                                {companyIdKey
                                    ? companyIdKey.find(e => e === columnItem.value) ? getCompanyName(item, companyIdKey.find(e => e === columnItem.value)) : getCaseInsensitive(item, columnItem.value)
                                    : columnItem.value === 'typeId' ? getCompanyType(getCaseInsensitive(item, columnItem.value)) : getCaseInsensitive(item, columnItem.value)}
                            </p>
                        </div>
                    </div>
                </td>
            })}
            {order && <td className="px-5 py-1 border-b border-gray-200">
                <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                    <div
                        className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                        style={{ width: `${(item["electronicHousingUnitIds"].length / item?.["orderParts"]?.[0]?.["count"]) * 100}%` }}> {(item["electronicHousingUnitIds"].length / item?.["orderParts"]?.[0]?.["count"]) * 100}%
                    </div>
                </div>
            </td>}

            {checkBox && <td className="px-5 py-1 border-b border-gray-200 w-[10px]">
                <div className="flex items-center">
                    <div className="form-check">
                        <input
                            className="form-check-input text-white appearance-none h-[14px] w-[14px] border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                            type="checkbox" value="" id="flexCheckChecked"
                            checked={checked ? checked : false}
                            onClick={event => event.stopPropagation()}
                            onChange={(e) => {
                                onChange(item)
                            }} />
                    </div>
                </div>
            </td>}

        </tr>)
};